/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaVacaciones.module.scss";

// =====================================================
// DATOS IPSOS VACACIONES (Base: 400 casos)
// =====================================================
const IPSOS_VACACIONES = {
  frecuenciaVacaciones: {
    variasVeces: { pct: 0.252, factor: 3, label: "Varias veces en el año" },
    dosVeces: { pct: 0.035, factor: 2, label: "2 veces al año" },
    unaVez: { pct: 0.601, factor: 1, label: "Una vez al año" },
    cadaDosAnos: { pct: 0.096, factor: 0.5, label: "Una vez cada dos años" },
    nunca: { pct: 0.016, factor: 0, label: "Nunca ha tomado vacaciones" },
  },
  aerolineas: {
    Latam: 0.827,
    Sky: 0.068,
    JetSmart: 0.004,
    "Sin preferencia": 0.1,
  },
  tipoDestino: {
    Nacional: 0.548,
    Internacional: 0.437,
    Regional: 0.016,
  },
};

const TICKETS_VACACIONES = [300000, 500000, 1000000, 2000000];

const TEMPORADA_OPCIONES = [
  { key: "Verano", pct: 0.546 },
  { key: "Otoño", pct: 0.204 },
  { key: "Invierno", pct: 0.153 },
  { key: "Primavera", pct: 0.097 },
];

const COLORES_AEROLINEAS = ["#0d6efd", "#6c757d", "#fd7e14", "#198754"];
const COLORES_DESTINO = ["#0d6efd", "#198754", "#fd7e14"];

const renderPieChart = (data, colors) => {
  let accumulated = 0;
  const segments = data.map(({ share }, i) => {
    const start = accumulated;
    accumulated += share * 100;
    return `${colors[i % colors.length]} ${start}% ${accumulated}%`;
  });
  const gradient = `conic-gradient(${segments.join(", ")})`;

  return (
    <div className={styles.pieChart} style={{ background: gradient }}>
      <div className={styles.pieChartInner} />
    </div>
  );
};

export const Vacaciones = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [ticketPromedio, setTicketPromedio] = useState(500000);
  const [verAnual, setVerAnual] = useState(false);
  const [anoCompleto, setAnoCompleto] = useState(true);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState("Verano");

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const data = IPSOS_VACACIONES;

    const desgloseFrecuencia = {};
    let totalViajes = 0;

    Object.entries(data.frecuenciaVacaciones).forEach(([freq, info]) => {
      const personas = Math.round(totalEjecutivos * info.pct);
      const viajes = Math.round(personas * info.factor);
      desgloseFrecuencia[freq] = { pct: info.pct, label: info.label, personas, viajes };
      totalViajes += viajes;
    });

    const temporadaPct = anoCompleto ? 1 : TEMPORADA_OPCIONES.find((t) => t.key === temporadaSeleccionada)?.pct || 1;
    const mercadoTemporada = Math.round(totalViajes * temporadaPct * ticketPromedio);

    const aerolineaData = Object.entries(data.aerolineas)
      .sort(([, a], [, b]) => b - a)
      .map(([nombre, share]) => ({
        nombre,
        share,
        clientes: Math.round(totalEjecutivos * share),
      }));

    const tipoDestinoData = Object.entries(data.tipoDestino)
      .sort(([, a], [, b]) => b - a)
      .map(([nombre, share]) => ({
        nombre,
        share,
        clientes: Math.round(totalEjecutivos * share),
      }));

    return {
      desgloseFrecuencia,
      totalViajes,
      totalViajesAnuales: totalViajes * 12,
      mercadoTemporada,
      mercadoTemporadaAnual: mercadoTemporada * 12,
      aerolineaData,
      tipoDestinoData,
    };
  }, [totalEjecutivos, ticketPromedio, anoCompleto, temporadaSeleccionada]);

  if (loading || edificios.length === 0) return <Loader />;

  const frecuenciaKeys = Object.keys(IPSOS_VACACIONES.frecuenciaVacaciones);

  return (
    <div className={styles.vacacionesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>VACACIONES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.vacacionesContent}>
        {/* Tabla de frecuencia de vacaciones */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de vacaciones</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Viajes {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  return (
                    <tr key={freq}>
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "nunca" ? "-" : formatNumber(verAnual ? data.viajes * 12 : data.viajes)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={2} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.totalViajesAnuales : resultados.totalViajes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Configuración: Temporada + Ticket + Mercado */}
        {resultados && (
          <>
            <div className={styles.configuracionRow}>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabelRow}>
                    <span className={styles.bloqueLabel}>Preferencia por temporada</span>
                    <label className={styles.anoCompletoCheck}>
                      <input type="checkbox" checked={anoCompleto} onChange={(e) => setAnoCompleto(e.target.checked)} />
                      <span>Año completo</span>
                    </label>
                  </div>
                  <div className={cx(styles.temporadaOpciones, anoCompleto && styles.temporadaDisabled)}>
                    {TEMPORADA_OPCIONES.map((t) => (
                      <label key={t.key} className={styles.opcionRadio}>
                        <span className={styles.badgeIpsos}>{(t.pct * 100).toFixed(0)}%</span>
                        <span>{t.key}</span>
                        <input
                          type="radio"
                          name="temporada"
                          disabled={anoCompleto}
                          checked={temporadaSeleccionada === t.key}
                          onChange={() => setTemporadaSeleccionada(t.key)}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabel}>Ticket promedio</div>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_VACACIONES.map((t) => (
                      <label key={t} className={styles.opcionRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t}
                          onChange={() => setTicketPromedio(t)}
                        />
                        <span>{formatLocal(convertCLP(t))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.mercadoRow}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>
                  Mercado {anoCompleto ? "anual" : temporadaSeleccionada} {verAnual ? "anual" : "mensual"}
                </span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoTemporadaAnual : resultados.mercadoTemporada))}
              </div>
            </div>
          </>
        )}

        {/* Gráficos: Aerolínea + Destino */}
        {resultados && (
          <div className={styles.pieChartsRow}>
            <div className={styles.pieChartContainer}>
              <h4>Aerolínea de preferencia</h4>
              <div className={styles.pieChartContent}>
                {renderPieChart(resultados.aerolineaData, COLORES_AEROLINEAS)}
                <div className={styles.pieLegend}>
                  {resultados.aerolineaData.map((d, i) => (
                    <div key={d.nombre} className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: COLORES_AEROLINEAS[i] }} />
                      <span className={styles.legendLabel}>{d.nombre}</span>
                      <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.pieChartContainer}>
              <h4>Tipo de destino</h4>
              <div className={styles.pieChartContent}>
                {renderPieChart(resultados.tipoDestinoData, COLORES_DESTINO)}
                <div className={styles.pieLegend}>
                  {resultados.tipoDestinoData.map((d, i) => (
                    <div key={d.nombre} className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: COLORES_DESTINO[i] }} />
                      <span className={styles.legendLabel}>{d.nombre}</span>
                      <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. El 60.1% toma vacaciones una vez al año. Latam
            concentra el 82.7% de preferencia en aerolíneas.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vacaciones;
